import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { useSession } from "../context/AuthContext";
import { useNavigate, Link, useParams } from "react-router-dom";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import api from "../services/api";

export default function AdminStaffDetail() {
  const { id } = useParams<{ id: string }>();
  const { status } = useSession();
  const navigate = useNavigate();
  
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'monthly' | 'daily'>('monthly');

  const fetchDetails = async () => {
    try {
      const res = await api.get(`/admin/staff-assessment/${id}`);
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch staff details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
       navigate("/login");
    } else if (status === "authenticated" && id) {
       fetchDetails();
    }
  }, [status, id, navigate]);

  if (loading || !data) {
    return (
      <div className="flex h-screen bg-background text-on-background">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-0 md:ml-20">
          <Header />
          <div className="flex-1 p-10 animate-pulse space-y-12 mt-16">
            <div className="h-64 bg-surface-container-low rounded-[3rem]" />
            <div className="grid grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="h-32 bg-surface-container-low rounded-[2rem]" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { staff, tasks, monthlyStats, trends } = data;
  const chartData = trends[timeframe];

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      
      <main className="ml-0 md:ml-20 pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          
          {/* Tactical Breadcrumb */}
          <nav className="flex items-center gap-2 px-2">
             <Link 
               to="/admin/staff-assessment" 
               className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-primary transition-colors flex items-center gap-1"
             >
                <span className="material-symbols-outlined text-[14px]">arrow_back</span>
                Staff Assessment
             </Link>
             <span className="text-slate-300">/</span>
             <span className="text-[10px] font-black uppercase tracking-widest text-primary truncate max-w-[200px]">
                {staff.name} Performance Audit
             </span>
          </nav>

          {/* Profile Header Card */}
          <div className="relative overflow-hidden bg-surface-container-low rounded-[3.5rem] p-12 border border-outline-variant/10 group">
             <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
             
             <div className="relative flex flex-col md:flex-row items-center gap-10">
                <div className={`w-36 h-36 rounded-[2.5rem] flex items-center justify-center text-4xl font-black shadow-2xl bg-primary text-surface overflow-hidden`}>
                   {staff.avatarUrl ? (
                     <img src={staff.avatarUrl} alt={staff.name} className="w-full h-full object-cover" />
                   ) : (
                     staff.name?.[0] || "S"
                   )}
                </div>
                
                <div className="flex-1 text-center md:text-left space-y-4">
                   <div>
                      <h1 className="text-4xl font-black tracking-tighter text-on-surface">{staff.name}</h1>
                      <p className="text-slate-500 font-medium">{staff.email}</p>
                   </div>
                   
                   <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                      <div className={`px-6 py-2 rounded-full flex items-center gap-2 border border-primary/20 bg-primary/10 font-bold text-xs uppercase tracking-widest text-primary`}>
                         <span className="material-symbols-outlined text-[14px]">verified_user</span>
                         {staff.role.replace("_", " ")}
                      </div>
                      <div className="px-6 py-2 rounded-full border border-outline-variant/10 bg-white/50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                         Clearance: Authorized
                      </div>
                   </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div className="bg-white p-6 rounded-[2rem] border border-outline-variant/10 text-center shadow-sm min-w-[150px]">
                      <span className="block text-2xl font-black text-emerald-500">98%</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tactical Health</span>
                   </div>
                   <div className="bg-white p-6 rounded-[2rem] border border-outline-variant/10 text-center shadow-sm min-w-[150px]">
                      <span className="block text-2xl font-black text-primary">{tasks.length}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Life-time Resolved</span>
                   </div>
                </div>
             </div>
          </div>

          {/* Performance Dashboard Section */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            
            {/* Left: Monthly Snapshot */}
            <div className="xl:col-span-1 space-y-8">
              <div className="flex items-center gap-4 px-2">
                 <div className="w-2 h-8 bg-primary rounded-full" />
                 <h2 className="text-2xl font-black tracking-tight text-on-surface">Monthly Snapshot</h2>
              </div>

              <div className="bg-surface-container-low p-8 rounded-[3rem] border border-outline-variant/10 shadow-inner space-y-8">
                <div className="grid grid-cols-1 gap-6">
                  <MetricCard 
                    label="Current Month Resolved" 
                    value={monthlyStats.total} 
                    subLabel="Tasks Completed"
                    icon="task_alt"
                    color="primary"
                  />
                  <MetricCard 
                    label="Avg Response" 
                    value={`${monthlyStats.avgAttend}m`} 
                    subLabel="Attend Time"
                    icon="avg_time"
                    color="emerald"
                  />
                  <MetricCard 
                    label="Avg Resolution" 
                    value={`${monthlyStats.avgComplete}m`} 
                    subLabel="Finish Time"
                    icon="speed"
                    color="indigo"
                  />
                </div>
                
                <div className="p-6 bg-white/50 rounded-2xl border border-outline-variant/10 italic text-[11px] font-medium text-slate-500 leading-relaxed">
                   "Monthly performance is calculated based on tasks resolved between the 1st and the current date of {new Date().toLocaleString('default', { month: 'long' })}."
                </div>
              </div>
            </div>

            {/* Right: Trend Chart */}
            <div className="xl:col-span-2 space-y-8">
              <div className="flex items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-8 bg-indigo-500 rounded-full" />
                  <h2 className="text-2xl font-black tracking-tight text-on-surface">Tactical Trend Analysis</h2>
                </div>
                
                <div className="flex bg-surface-container-high p-1.5 rounded-2xl border border-outline-variant/10 shadow-inner">
                  <button 
                    onClick={() => setTimeframe('monthly')}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      timeframe === 'monthly' ? 'bg-primary text-surface shadow-lg' : 'text-slate-400 hover:text-on-surface'
                    }`}
                  >
                    6 Months
                  </button>
                  <button 
                    onClick={() => setTimeframe('daily')}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      timeframe === 'daily' ? 'bg-primary text-surface shadow-lg' : 'text-slate-400 hover:text-on-surface'
                    }`}
                  >
                    Current Month
                  </button>
                </div>
              </div>

              <div className="bg-surface-container-low p-8 rounded-[3rem] border border-outline-variant/10 shadow-inner h-[460px] flex flex-col">
                <div className="flex-1 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                      <XAxis 
                        dataKey="label" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }}
                        dy={10}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          borderRadius: '1rem', 
                          border: 'none', 
                          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                          padding: '1rem'
                        }}
                        itemStyle={{ fontWeight: 900, fontSize: '12px' }}
                        labelStyle={{ fontWeight: 900, marginBottom: '0.5rem', color: '#64748b' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="count" 
                        stroke="var(--color-primary)" 
                        strokeWidth={4}
                        fillOpacity={1} 
                        fill="url(#colorCount)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex justify-center gap-8">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Resolution Volume ({timeframe === 'monthly' ? 'Monthly' : 'Daily'})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Execution Ledger */}
          <div className="space-y-8">
            <div className="flex items-center gap-4 px-2">
               <div className="w-2 h-8 bg-emerald-500 rounded-full" />
               <h2 className="text-2xl font-black tracking-tight text-on-surface">Execution Ledger</h2>
            </div>

            <div className="bg-surface-container-low rounded-[3.5rem] border border-outline-variant/10 overflow-hidden shadow-inner">
               <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-surface-container-low border-b border-outline-variant/10 z-10">
                      <tr>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Type</th>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Task Title</th>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Student</th>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Attend</th>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Finish</th>
                        <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/5">
                      {tasks.length > 0 ? tasks.map((task: any) => (
                        <tr key={task.id} className="hover:bg-white/50 transition-colors group">
                          <td className="px-8 py-6">
                            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                              task.type === "Assistance" ? "bg-indigo-50 text-indigo-600 border-indigo-100" : "bg-emerald-50 text-emerald-600 border-emerald-100"
                            }`}>
                              {task.type}
                            </span>
                          </td>
                          <td className="px-8 py-6">
                            <span className="font-black text-sm text-on-surface">{task.taskTitle}</span>
                          </td>
                          <td className="px-8 py-6">
                            <span className="text-xs font-bold text-slate-500">{task.studentName}</span>
                          </td>
                          <td className="px-8 py-6">
                            <span className="text-sm font-black text-primary">{task.timeToAttend}m</span>
                          </td>
                          <td className="px-8 py-6">
                            <span className="text-sm font-black text-indigo-500">{task.timeToComplete}m</span>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{new Date(task.finishedAt).toLocaleDateString()}</span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={6} className="py-20 text-center opacity-30 italic font-medium">
                            No historical resolution data available for this operative.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
               </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MetricCard({ label, value, subLabel, icon, color }: { label: string; value: string | number; subLabel: string; icon: string; color: string }) {
  const colorMap: any = {
    primary: 'bg-primary text-surface shadow-primary/20',
    emerald: 'bg-emerald-500 text-surface shadow-emerald-500/20',
    indigo: 'bg-indigo-500 text-surface shadow-indigo-500/20',
  };

  return (
    <div className={`p-8 rounded-[2rem] bg-white border border-outline-variant/10 shadow-sm flex items-center gap-6 group hover:shadow-xl transition-all`}>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${colorMap[color]}`}>
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-black text-on-surface tracking-tight">{value}</span>
          <span className="text-[10px] font-bold text-slate-500">{subLabel}</span>
        </div>
      </div>
    </div>
  );
}
